import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { useAuth } from "../hooks/useAuth";
import BalanceDisplay from "./BalanceDisplay";
import AddExpenseForm from "./AddExpenseForm";
import ExpenseList from "./ExpenseList";
import SettleUp from "./SettleUp";
import PartnerSetup from "./onboarding/PartnerSetup";
import InvitePartner from "./InvitePartner";
import Logo from "./Logo";

const ICONS = {
    overview: (
        <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor" />
            <rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor" />
            <rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor" />
            <rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor" />
        </svg>
    ),
    addExpense: (
        <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    ),
    history: (
        <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 4.5h10M3 8h10M3 11.5h10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
    ),
    settings: (
        <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.25" />
            <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.1 3.1l1.4 1.4M11.5 11.5l1.4 1.4M3.1 12.9l1.4-1.4M11.5 4.5l1.4-1.4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
    ),
};

const NAV_ITEMS = [
    { id: "overview", label: "Overview" },
    { id: "addExpense", label: "Add Expense" },
    { id: "history", label: "History" },
    { id: "settings", label: "Settings" },
];

const TAB_TITLES = {
    overview: "Overview",
    addExpense: "Add Expense",
    history: "History",
    settings: "Settings",
};

export default function Dashboard() {
    const { user, signOut } = useAuth();
    const [expenses, setExpenses] = useState([]);
    const [settlements, setSettlements] = useState([]);
    const [couple, setCouple] = useState(null);
    const [needsOnboarding, setNeedsOnboarding] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState("overview");

    // Pagination state
    const [currentPage, setCurrentPage] = useState(0);
    const [totalExpenseCount, setTotalExpenseCount] = useState(0);
    const [totalSettlementCount, setTotalSettlementCount] = useState(0);
    const [hasMoreItems, setHasMoreItems] = useState(true);

    const handleSignOut = async () => {
        await signOut();
    };

    const displayName =
        user?.user_metadata?.display_name || user?.email || "User";

    /**
     * Fetch paginated history data for a specific page number
     * Combines expenses and settlements, fetching 10 items per page
     *
     * @param {number} pageNumber - Page to fetch (0-indexed, so 0 = first page)
     */
    const fetchDataPage = useCallback(
        async (pageNumber) => {
            if (!couple?.id) return;

            try {
                setLoading(true);
                setError(null);

                const itemsPerPage = 10;
                const offset = pageNumber * itemsPerPage;

                // Fetch expenses for this page with exact count
                const [expensesResult, settlementsResult] = await Promise.all([
                    supabase
                        .from("expenses")
                        .select("*", { count: "exact" })
                        .eq("couple_id", couple.id)
                        .order("created_at", { ascending: false })
                        .range(offset, offset + itemsPerPage - 1),
                    supabase
                        .from("settlements")
                        .select("*", { count: "exact" })
                        .eq("couple_id", couple.id)
                        .order("settled_at", { ascending: false })
                        .range(offset, offset + itemsPerPage - 1),
                ]);

                if (expensesResult.error) throw expensesResult.error;
                if (settlementsResult.error) throw settlementsResult.error;

                // Store total counts (used to determine if more pages exist)
                setTotalExpenseCount(expensesResult.count || 0);
                setTotalSettlementCount(settlementsResult.count || 0);

                // Update displayed items
                setExpenses(expensesResult.data || []);
                setSettlements(settlementsResult.data || []);

                // Calculate if there are more items beyond current page
                const totalItems =
                    (expensesResult.count || 0) + (settlementsResult.count || 0);
                const maxPages = Math.ceil(totalItems / itemsPerPage);
                setHasMoreItems(pageNumber < maxPages - 1);
            } catch (err) {
                console.error("Error fetching paginated data:", err);
                setError(
                    "Failed to load data. Please check your Supabase configuration.",
                );
            } finally {
                setLoading(false);
            }
        },
        [couple?.id],
    );

    // Fetch couple data when user loads
    useEffect(() => {
        const fetchCouple = async () => {
            if (!user) return;

            try {
                const { data, error } = await supabase
                    .from("couple_members")
                    .select(
                        `
            *,
            couple:couples(
              *,
              members:couple_members(*)
            )
          `,
                    )
                    .eq("user_id", user.id)
                    .single();

                if (error) {
                    if (error.code === "PGRST116") {
                        // No couple found, need onboarding
                        setNeedsOnboarding(true);
                    } else {
                        throw error;
                    }
                } else if (data) {
                    setCouple(data.couple);
                    setNeedsOnboarding(false);
                }
            } catch (err) {
                console.error("Error fetching couple:", err);
            }
        };

        fetchCouple();
    }, [user]);

    // Calculate balance from current user's perspective
    const calculateBalance = () => {
        if (!user) return 0;

        let balance = 0;

        // Calculate from expenses
        expenses.forEach((expense) => {
            const owedAmount =
                expense.split_amount !== null
                    ? parseFloat(expense.split_amount)
                    : parseFloat(expense.amount) / 2;

            if (expense.paid_by === user.id) {
                // I paid, so partner owes me
                balance += owedAmount;
            } else {
                // Partner paid, so I owe them
                balance -= owedAmount;
            }
        });

        // Apply settlements
        settlements.forEach((settlement) => {
            if (settlement.settled_by === user.id) {
                // I settled, reducing what I'm owed
                balance -= parseFloat(settlement.amount);
            } else {
                // Partner settled, reducing what they're owed
                balance += parseFloat(settlement.amount);
            }
        });

        return balance;
    };

    // Fetch initial data
    useEffect(() => {
        if (couple?.id) {
            // Always start at page 0 when couple data loads
            setCurrentPage(0);
            fetchDataPage(0);
        }
    }, [couple?.id, fetchDataPage]);

    // Set up real-time subscriptions
    useEffect(() => {
        if (!couple?.id) return;

        const refreshCurrentPage = () => {
            fetchDataPage(currentPage);
        };

        // Subscribe to expenses changes
        const expensesSubscription = supabase
            .channel(`expenses-channel-${couple.id}`)
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "expenses",
                    filter: `couple_id=eq.${couple.id}`,
                },
                refreshCurrentPage,
            )
            .subscribe();

        // Subscribe to settlements changes
        const settlementsSubscription = supabase
            .channel(`settlements-channel-${couple.id}`)
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "settlements",
                    filter: `couple_id=eq.${couple.id}`,
                },
                refreshCurrentPage,
            )
            .subscribe();

        // Cleanup subscriptions on unmount
        return () => {
            supabase.removeChannel(expensesSubscription);
            supabase.removeChannel(settlementsSubscription);
        };
    }, [couple?.id, currentPage, fetchDataPage]);

    const addExpense = async (expenseData) => {
        if (!couple?.id) return;

        try {
            // Ensure couple_id is included
            const expenseWithCouple = {
                ...expenseData,
                couple_id: couple.id,
            };

            const { error } = await supabase
                .from("expenses")
                .insert([expenseWithCouple]);

            if (error) throw error;
            // Real-time subscription will handle adding to state
        } catch (err) {
            console.error("Error adding expense:", err);
            throw err;
        }
    };

    const deleteExpense = async (id) => {
        if (!couple?.id) return;

        // Optimistic update
        const deletedExpense = expenses.find((exp) => exp.id === id);
        setExpenses((prev) => prev.filter((exp) => exp.id !== id));

        try {
            const { error } = await supabase
                .from("expenses")
                .delete()
                .eq("id", id)
                .eq("couple_id", couple.id);

            if (error) throw error;
        } catch (err) {
            console.error("Error deleting expense:", err);
            // Restore expense on error
            if (deletedExpense) {
                setExpenses((prev) => [deletedExpense, ...prev]);
            }
        }
    };

    const settleUp = async () => {
        if (!couple?.id || !user?.id) return;

        const currentBalance = calculateBalance();
        if (currentBalance === 0) return;

        // Get partner's ID
        const partnerId = couple.members.find(
            (m) => m.user_id !== user.id,
        )?.user_id;

        if (currentBalance < 0 && !partnerId) {
            return;
        }

        const settlementData = {
            amount: Math.abs(currentBalance),
            // When currentBalance > 0, partner owes us, so we receive payment
            // settled_by = user.id means this settlement reduces what we're owed
            settled_by: currentBalance > 0 ? user.id : partnerId,
            couple_id: couple.id,
        };

        try {
            const { error } = await supabase
                .from("settlements")
                .insert([settlementData]);

            if (error) throw error;
            // Real-time subscription will handle adding to state
        } catch (err) {
            console.error("Error settling:", err);
            throw err;
        }
    };

    /**
     * Navigate to previous page
     * Disabled when on first page (currentPage = 0)
     */
    const goToPreviousPage = async () => {
        if (currentPage > 0) {
            const newPage = currentPage - 1;
            setCurrentPage(newPage);
            await fetchDataPage(newPage);
        }
    };

    /**
     * Navigate to next page
     * Disabled when no more items exist (hasMoreItems = false)
     */
    const goToNextPage = async () => {
        if (hasMoreItems) {
            const newPage = currentPage + 1;
            setCurrentPage(newPage);
            await fetchDataPage(newPage);
        }
    };

    const balance = calculateBalance();

    // Handle onboarding completion
    const handleOnboardingComplete = (newCouple) => {
        setCouple(newCouple);
        setNeedsOnboarding(false);
    };

    return (
        <>
            {needsOnboarding ? (
                <PartnerSetup user={user} onComplete={handleOnboardingComplete} />
            ) : (
                <div className="app-shell">
                    <aside className="app-sidebar">
                        <div className="sidebar-brand">
                            <div className="sidebar-brand-mark">
                                <Logo width={20} height={20} />
                            </div>
                            <span className="sidebar-brand-name">GoHalfsies</span>
                        </div>

                        <nav className="sidebar-nav">
                            {NAV_ITEMS.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={`nav-btn ${activeTab === item.id ? "active" : ""}`}
                                    onClick={() => setActiveTab(item.id)}
                                >
                                    <span className="nav-icon">{ICONS[item.id]}</span>
                                    {item.label}
                                </button>
                            ))}
                        </nav>

                        <div className="sidebar-footer">
                            <div className="sidebar-month-label">{displayName}</div>
                            <button type="button" className="nav-btn" onClick={handleSignOut}>
                                Sign out
                            </button>
                        </div>
                    </aside>

                    <div className="app-content">
                        <header className="content-header">
                            <h1 className="content-title">{TAB_TITLES[activeTab]}</h1>
                        </header>

                        <main className="content-body">
                            {error && (
                                <div className="auth-error" style={{ marginBottom: 0 }}>
                                    {error}{" "}
                                    <button
                                        type="button"
                                        className="auth-switch-btn"
                                        onClick={() => fetchDataPage(currentPage)}
                                    >
                                        Retry
                                    </button>
                                </div>
                            )}

                            {activeTab === "overview" && (
                                <>
                                    {couple?.members?.length < 2 && (
                                        <InvitePartner inviteCode={couple.invite_code} />
                                    )}
                                    {couple?.members?.length >= 2 && (
                                        <>
                                            <BalanceDisplay
                                                balance={balance}
                                                loading={loading}
                                                couple={couple}
                                                currentUserId={user?.id}
                                            />
                                            <SettleUp
                                                balance={balance}
                                                onSettle={settleUp}
                                                disabled={loading}
                                                couple={couple}
                                                currentUserId={user?.id}
                                            />
                                        </>
                                    )}
                                </>
                            )}

                            {activeTab === "addExpense" && (
                                <AddExpenseForm
                                    onAddExpense={addExpense}
                                    disabled={loading}
                                    couple={couple}
                                    currentUserId={user?.id}
                                />
                            )}

                            {activeTab === "history" && (
                                <ExpenseList
                                    expenses={expenses}
                                    settlements={settlements}
                                    onDeleteExpense={deleteExpense}
                                    loading={loading}
                                    couple={couple}
                                    currentUserId={user?.id}
                                    currentPage={currentPage}
                                    hasMoreItems={hasMoreItems}
                                    onPreviousPage={goToPreviousPage}
                                    onNextPage={goToNextPage}
                                    totalItems={totalExpenseCount + totalSettlementCount}
                                />
                            )}

                            {activeTab === "settings" && (
                                <section className="section-card">
                                    <div className="section-card-header">
                                        <h2 className="section-card-title">Account</h2>
                                    </div>
                                    <div className="section-card-body" style={{ padding: "16px 20px" }}>
                                        <div style={{ marginBottom: "14px", color: "var(--text-2)" }}>
                                            Signed in as <strong style={{ color: "var(--text-1)" }}>{displayName}</strong>
                                        </div>
                                        <button type="button" className="submit-btn" onClick={handleSignOut}>
                                            Sign Out
                                        </button>
                                    </div>
                                </section>
                            )}
                        </main>
                    </div>

                    <nav className="mobile-bottom-nav">
                        {NAV_ITEMS.map((item) => (
                            <button
                                key={item.id}
                            type="button"
                            className={`mobile-nav-btn ${activeTab === item.id ? "active" : ""}`}
                            onClick={() => setActiveTab(item.id)}
                        >
                            {ICONS[item.id]}
                            {item.label}
                        </button>
                    ))}
                    </nav>
                </div>
            )}
        </>
    );
}
